import React, {
  useState,
  useCallback,
  useRef,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";
import {
  Box,
  TextField,
  IconButton,
  InputAdornment,
  Typography,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  Send as SendIcon,
  Add as AddIcon,
  Mic as MicIcon,
  Close as CloseIcon,
  Reply as ReplyIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { InputToolbarProps } from "../../../types/chat";
import ChatActionMenu from "./ChatActionMenu";

export interface InputToolbarMethods {
  focus: () => void;
}

const InputToolbar = forwardRef<InputToolbarMethods, InputToolbarProps>(
  (
    {
      onSendText,
      onStartRecording,
      onAttachmentPress,
      onSelectImage,
      onSelectVideo,
      onSelectDocument,
      placeholder = "Type a message...",
      disabled = false,
      replyToMessage,
      onCancelReply,
      groupId, // Used for draft message storage
      hideAudioControls = false,
      hideAttachmentControls = false,
      // actions = [], // TODO: Implement action menu for attachments, voice recording, etc.
    },
    ref,
  ) => {
    // Generate storage key for draft messages
    const draftKey = `draftMessage-${groupId}`;

    // Initialize text state with saved draft or empty string
    const [text, setText] = useState(() => {
      return groupId ? localStorage.getItem(draftKey) || "" : "";
    });
    const [isSending, setIsSending] = useState(false);
    const [showActionMenu, setShowActionMenu] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Expose focus method to parent components
    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          inputRef.current?.focus();
        },
      }),
      [],
    );

    // Load draft message when groupId changes
    useEffect(() => {
      if (groupId) {
        const savedDraft = localStorage.getItem(draftKey) || "";
        setText(savedDraft);
      } else {
        setText("");
      }
    }, [groupId, draftKey]);

    // Save draft to localStorage whenever text changes (debounced)
    const saveDraft = useCallback(
      (value: string) => {
        if (groupId) {
          if (value.trim()) {
            localStorage.setItem(draftKey, value);
          } else {
            localStorage.removeItem(draftKey);
          }
        }
      },
      [groupId, draftKey],
    );

    // Handle text change with draft saving
    const handleTextChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setText(newValue);
        saveDraft(newValue);
      },
      [saveDraft],
    );

    // Clear draft manually
    const handleClearDraft = useCallback(() => {
      setText("");
      if (groupId) {
        localStorage.removeItem(draftKey);
      }
      inputRef.current?.focus();
    }, [groupId, draftKey]);

    const handleSend = useCallback(async () => {
      if (!text.trim() || disabled || isSending) return;

      setIsSending(true);
      try {
        await onSendText(text.trim());
        setText("");
        // Clear draft from localStorage when message is sent
        if (groupId) {
          localStorage.removeItem(draftKey);
        }
        // Refocus the input after sending
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      } catch (error) {
        console.error("Failed to send message:", error);
        // TODO: Show error toast/notification
      } finally {
        setIsSending(false);
      }
    }, [text, disabled, isSending, onSendText, groupId, draftKey]);

    // Handle keyboard shortcuts
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          handleSend();
        }
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          handleSend();
        }
      },
      [handleSend],
    );

    const handleAttachmentPress = useCallback(() => {
      setShowActionMenu(true);
      onAttachmentPress?.();
    }, [onAttachmentPress]);

    const handleCloseActionMenu = useCallback(() => {
      setShowActionMenu(false);
    }, []);

    const handleSelectImage = useCallback(
      async (file: File) => {
        if (onSelectImage) {
          try {
            await onSelectImage(file);
          } catch (error) {
            console.error("Failed to upload image:", error);
            // TODO: Show error toast
          }
        }
      },
      [onSelectImage],
    );

    const handleSelectVideo = useCallback(
      async (file: File) => {
        if (onSelectVideo) {
          try {
            await onSelectVideo(file);
          } catch (error) {
            console.error("Failed to upload video:", error);
            // TODO: Show error toast
          }
        }
      },
      [onSelectVideo],
    );

    const handleSelectDocument = useCallback(
      async (file: File) => {
        if (onSelectDocument) {
          try {
            await onSelectDocument(file);
          } catch (error) {
            console.error("Failed to upload document:", error);
            // TODO: Show error toast
          }
        }
      },
      [onSelectDocument],
    );

    const handleStartAudioRecording = useCallback(() => {
      setShowActionMenu(false);
      onStartRecording?.();
    }, [onStartRecording]);

    return (
      <>
        {/* Reply Preview */}
        {replyToMessage && (
          <Paper
            elevation={1}
            sx={{
              backgroundColor: "#f8f9fa",
              borderLeft: "4px solid #2563EB",
              p: 2,
              mx: 2,
              borderRadius: "8px 8px 0 0",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ReplyIcon sx={{ fontSize: 16, color: "#2563EB" }} />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: "#2563EB" }}
                >
                  Replying to {replyToMessage.fromUser.firstName}{" "}
                  {replyToMessage.fromUser.lastName}
                </Typography>
              </Box>
              <IconButton size="small" onClick={onCancelReply}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                color: "#6B7280",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "300px",
              }}
            >
              {replyToMessage.message}
            </Typography>
          </Paper>
        )}

        <Box
          sx={{
            borderTop: replyToMessage
              ? "none"
              : "1px solid rgba(0, 119, 200, 0.1)",
            backgroundColor: "#ffffff",
            background: "linear-gradient(135deg, #ffffff 0%, #f6f7fb 100%)",
            p: 2,
            display: "flex",
            alignItems: "flex-end",
            gap: 1,
            flexShrink: 0, // Prevent shrinking
            borderRadius: replyToMessage ? "0 0 8px 8px" : "none",
            // Removed sticky positioning that was causing scroll issues
          }}
          data-testid="input-toolbar"
        >
          {/* Actions button */}
          {!hideAttachmentControls && (
            <IconButton
              onClick={handleAttachmentPress}
              disabled={disabled}
              sx={{
                color: showActionMenu ? "primary.main" : "secondary.main",
                backgroundColor: showActionMenu
                  ? "rgba(25, 118, 210, 0.08)"
                  : "transparent",
                transform: showActionMenu ? "rotate(45deg)" : "rotate(0deg)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  backgroundColor: "rgba(37, 99, 235, 0.08)",
                  transform: showActionMenu
                    ? "rotate(45deg) scale(1.1)"
                    : "rotate(0deg) scale(1.1)",
                },
              }}
            >
              <AddIcon />
            </IconButton>
          )}

          {/* Text input */}
          <TextField
            fullWidth
            multiline
            maxRows={4}
            value={text}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || isSending}
            variant="outlined"
            size="small"
            inputRef={inputRef}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {/* Clear draft button - only show when there's text */}
                  {text.length > 0 && (
                    <Tooltip title="Clear message">
                      <IconButton
                        size="small"
                        onClick={handleClearDraft}
                        sx={{
                          color: "text.secondary",
                          "&:hover": {
                            color: "error.main",
                          },
                          mr: 0.5,
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <IconButton
                    onClick={handleSend}
                    disabled={!text.trim() || disabled || isSending}
                    color="primary"
                    sx={{
                      opacity: text.trim() ? 1 : 0.5,
                      transition: "opacity 0.2s ease",
                    }}
                  >
                    <SendIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                backgroundColor: "#f9fafb",
                "&:hover": {
                  backgroundColor: "#ffffff",
                },
                "&.Mui-focused": {
                  backgroundColor: "#ffffff",
                },
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                lineHeight: 1.4,
              },
            }}
          />

          {/* Dedicated Voice Recording Button */}
          {!hideAudioControls && (
            <IconButton
              onClick={handleStartAudioRecording}
              disabled={disabled}
              sx={{
                color: "error.main",
                backgroundColor: "rgba(211, 47, 47, 0.08)",
                border: "1px solid rgba(211, 47, 47, 0.2)",
                borderRadius: 2,
                width: 44,
                height: 44,
                "&:hover": {
                  backgroundColor: "rgba(211, 47, 47, 0.12)",
                  transform: "scale(1.05)",
                },
                "&:active": {
                  transform: "scale(0.95)",
                },
                transition: "all 0.2s ease",
              }}
              title="Record voice message"
            >
              <MicIcon />
            </IconButton>
          )}

          {/* Helper text for keyboard shortcuts and draft info */}
          {text.length > 0 && (
            <Typography
              variant="caption"
              sx={{
                ml: 1,
                color: "text.secondary",
                fontSize: "11px",
                opacity: 0.6,
                alignSelf: "center",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              Enter to send • Draft saved
            </Typography>
          )}
          {text.length === 0 && groupId && localStorage.getItem(draftKey) && (
            <Typography
              variant="caption"
              sx={{
                ml: 1,
                color: "info.main",
                fontSize: "11px",
                opacity: 0.7,
                alignSelf: "center",
              }}
            >
              Draft restored
            </Typography>
          )}
        </Box>

        {/* Action Menu */}
        {!hideAttachmentControls && (
          <ChatActionMenu
            show={showActionMenu}
            onClose={handleCloseActionMenu}
            onSelectImage={handleSelectImage}
            onSelectVideo={handleSelectVideo}
            onSelectDocument={handleSelectDocument}
            onStartAudioRecording={handleStartAudioRecording}
          />
        )}
      </>
    );
  },
);

InputToolbar.displayName = "InputToolbar";

export default InputToolbar;
